// Manual Entry — period grid (docs/03-pages.md §7).
//
// Spreadsheet-style: rows = active manual-pool accounts, columns = balance
// + notes for the selected period. Period selector at top. Prior locked
// periods render read-only. Bulk save submits to /manual-entry/:period/bulk.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError, balances as balancesApi, manualEntry } from '@/lib/api';
import { useMe } from '@/hooks/useMe';
import type { ManualEntryRow } from '@frt/shared-types';

interface DraftRow {
  account_id: number;
  amount: string;
  /** YYYY-MM-DD; empty = use server default (period-end). Editable per row
   *  so admins can capture an intra-month "current" balance that lands in
   *  Dashboard / report Current totals. */
  as_of_date: string;
  notes: string;
  /** original status from server; lets us show "submitted" badges. */
  serverStatus: ManualEntryRow['status'];
  serverBalanceId: number | null;
}

function thisMonth(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function periodStartDate(p: string): string {
  return `${p}-01`;
}

function periodEndDate(p: string): string {
  const [y, m] = p.split('-').map(Number) as [number, number];
  // Last day of the period: day 0 of the next month.
  const last = new Date(Date.UTC(y, m, 0));
  return last.toISOString().slice(0, 10);
}

/** Default as-of date: today clamped into the period boundaries. For the
 *  current month this gives "today"; for past months it gives period-end;
 *  for future months it gives period-start. Mirrors what an operator
 *  intuitively expects when opening the grid. */
function defaultAsOf(period: string): string {
  const today = todayIso();
  const start = periodStartDate(period);
  const end = periodEndDate(period);
  if (today < start) return start;
  if (today > end) return end;
  return today;
}

function priorPeriod(p: string): string {
  const [y, m] = p.split('-').map(Number) as [number, number];
  const newM = m === 1 ? 12 : m - 1;
  const newY = m === 1 ? y - 1 : y;
  return `${newY}-${String(newM).padStart(2, '0')}`;
}

function nextPeriod(p: string): string {
  const [y, m] = p.split('-').map(Number) as [number, number];
  const newM = m === 12 ? 1 : m + 1;
  const newY = m === 12 ? y + 1 : y;
  return `${newY}-${String(newM).padStart(2, '0')}`;
}

export function PeriodGridPage() {
  const { data: me } = useMe();
  const [period, setPeriod] = useState(thisMonth());
  const queryClient = useQueryClient();

  const view = useQuery({
    queryKey: ['manual-entry', period],
    queryFn: () => manualEntry.view(period),
  });

  const [draftByAccountId, setDraftByAccountId] = useState<Record<number, DraftRow>>({});

  // Re-seed drafts whenever the server view re-loads.
  useEffect(() => {
    if (!view.data) return;
    const next: Record<number, DraftRow> = {};
    const fallback = defaultAsOf(period);
    for (const row of view.data.rows) {
      next[row.account_id] = {
        account_id: row.account_id,
        amount: row.balance ? String(row.balance.amount) : '',
        as_of_date: row.balance?.as_of_date ?? fallback,
        notes: row.balance?.notes ?? '',
        serverStatus: row.status,
        serverBalanceId: row.balance?.id ?? null,
      };
    }
    setDraftByAccountId(next);
  }, [view.data, period]);

  const isLocked = view.data?.is_locked === true;
  const canBulkSubmit = me?.role === 'admin' || me?.role === 'senior';

  const dirty = useMemo(() => {
    if (!view.data) return [] as DraftRow[];
    const out: DraftRow[] = [];
    const fallback = defaultAsOf(period);
    for (const row of view.data.rows) {
      const draft = draftByAccountId[row.account_id];
      if (!draft) continue;
      const orig = {
        amount: row.balance ? String(row.balance.amount) : '',
        as_of_date: row.balance?.as_of_date ?? fallback,
        notes: row.balance?.notes ?? '',
      };
      const changed =
        draft.amount !== orig.amount ||
        draft.notes !== orig.notes ||
        draft.as_of_date !== orig.as_of_date;
      if (changed && draft.amount !== '' && Number.isFinite(Number(draft.amount))) {
        out.push(draft);
      }
    }
    return out;
  }, [view.data, draftByAccountId, period]);

  const bulkSave = useMutation({
    mutationFn: () =>
      manualEntry.bulk(period, {
        rows: dirty.map((d) => ({
          account_id: d.account_id,
          amount: Number(d.amount),
          as_of_date: d.as_of_date || null,
          notes: d.notes ? d.notes : null,
        })),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['manual-entry', period] }),
  });

  const removeBalance = useMutation({
    mutationFn: (balanceId: number) => balancesApi.remove(balanceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['manual-entry', period] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Manual Entry</h1>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setPeriod(priorPeriod(period))}>
            ← Prior
          </Button>
          <Input
            className="w-32 text-center font-mono"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
          <Button variant="ghost" size="sm" onClick={() => setPeriod(nextPeriod(period))}>
            Next →
          </Button>
        </div>
      </div>

      {view.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
      {view.error instanceof ApiError && (
        <div className="text-sm text-red-600">
          {view.error.code}: {view.error.message}
        </div>
      )}

      {view.data && (
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle>
              Period {period}
              {isLocked && (
                <span className="ml-2 inline-block rounded bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">
                  LOCKED (frozen snapshot exists)
                </span>
              )}
            </CardTitle>
            {canBulkSubmit && !isLocked && (
              <Button
                onClick={() => bulkSave.mutate()}
                disabled={dirty.length === 0 || bulkSave.isPending}
              >
                {bulkSave.isPending ? 'Saving…' : `Save ${dirty.length} change(s)`}
              </Button>
            )}
          </CardHeader>
          <CardBody>
            {bulkSave.error instanceof ApiError && (
              <div className="mb-3 rounded bg-red-50 p-2 text-sm text-red-700">
                {bulkSave.error.code}: {bulkSave.error.message}
              </div>
            )}
            <p className="mb-3 text-xs text-slate-500">
              As Of defaults to today (or period-end for past periods). Use any date
              within {period} to record either a current or month-end balance.
            </p>
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="py-2 pr-3">Customer</th>
                  <th className="py-2 pr-3">Account</th>
                  <th className="py-2 pr-3">On Point</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">As Of</th>
                  <th className="py-2 pr-3">Amount</th>
                  <th className="py-2 pr-3">Notes</th>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {view.data.rows.map((row) => {
                  const draft = draftByAccountId[row.account_id];
                  return (
                    <tr key={row.account_id} className="border-t border-slate-100 align-top">
                      <td className="py-2 pr-3 font-mono text-xs text-slate-500">
                        {row.frt_customer_code}
                      </td>
                      <td className="py-2 pr-3 font-mono">{row.display_name}</td>
                      <td className="py-2 pr-3 text-slate-700">
                        {row.on_point_tester_name ?? '—'}
                      </td>
                      <td className="py-2 pr-3">
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="py-2 pr-3">
                        <Input
                          type="date"
                          disabled={isLocked}
                          min={periodStartDate(period)}
                          max={periodEndDate(period)}
                          value={draft?.as_of_date ?? ''}
                          onChange={(e) =>
                            setDraftByAccountId((prev) => ({
                              ...prev,
                              [row.account_id]: {
                                ...(prev[row.account_id] ?? {
                                  account_id: row.account_id,
                                  amount: '',
                                  as_of_date: defaultAsOf(period),
                                  notes: '',
                                  serverStatus: row.status,
                                  serverBalanceId: row.balance?.id ?? null,
                                }),
                                as_of_date: e.target.value,
                              },
                            }))
                          }
                          className="w-36"
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <Input
                          inputMode="decimal"
                          disabled={isLocked}
                          value={draft?.amount ?? ''}
                          onChange={(e) =>
                            setDraftByAccountId((prev) => ({
                              ...prev,
                              [row.account_id]: {
                                ...(prev[row.account_id] ?? {
                                  account_id: row.account_id,
                                  amount: '',
                                  as_of_date: defaultAsOf(period),
                                  notes: '',
                                  serverStatus: row.status,
                                  serverBalanceId: row.balance?.id ?? null,
                                }),
                                amount: e.target.value,
                              },
                            }))
                          }
                          className="w-28"
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <Input
                          disabled={isLocked}
                          value={draft?.notes ?? ''}
                          onChange={(e) =>
                            setDraftByAccountId((prev) => ({
                              ...prev,
                              [row.account_id]: {
                                ...(prev[row.account_id] ?? {
                                  account_id: row.account_id,
                                  amount: '',
                                  as_of_date: defaultAsOf(period),
                                  notes: '',
                                  serverStatus: row.status,
                                  serverBalanceId: row.balance?.id ?? null,
                                }),
                                notes: e.target.value,
                              },
                            }))
                          }
                        />
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {row.balance && canBulkSubmit && !isLocked && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => removeBalance.mutate(row.balance!.id)}
                            disabled={removeBalance.isPending}
                          >
                            Delete
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {view.data.rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-6 text-center text-slate-500">
                      No active manual-pool accounts.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: ManualEntryRow['status'] }) {
  const style =
    status === 'open'
      ? 'bg-amber-100 text-amber-800'
      : status === 'submitted'
        ? 'bg-emerald-100 text-emerald-800'
        : 'bg-slate-200 text-slate-700';
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${style}`}>
      {status}
    </span>
  );
}
